import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

const SESSION_TOKEN_KEY = "guestly_cart_session";

function getSessionToken(): string {
  let token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  }
  return token;
}

export interface CartItem {
  id: string;
  experience_id: string;
  slot_id: string;
  adult_count: number;
  child_count: number;
  toddler_count: number;
  guest_count: number;
  unit_price_snapshot: number | null;
  experiences: {
    id: string;
    title: string;
    slug: string;
    images: string[] | null;
    pricing_model: string;
    net_price_per_person: number | null;
    net_price_per_group: number | null;
    net_price_per_child: number | null;
    net_price_per_toddler: number | null;
    provider_id: string;
    location: string | null;
    duration_minutes: number;
    min_group_size: number | null;
    max_group_size: number | null;
  };
  slots: {
    id: string;
    date: string;
    start_time: string;
    end_time: string;
    capacity_remaining: number;
    is_available: boolean;
  };
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [cartId, setCartId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const sessionToken = useMemo(() => getSessionToken(), []);

  const itemCount = items.length;

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-cart", {
        body: { action: "get", session_token: sessionToken },
      });
      if (!error && data) {
        setCartId(data.cart_id);
        setItems(data.items ?? []);
      }
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, refreshKey]);

  const addItem = useCallback(
    async (params: {
      experience_id: string;
      slot_id: string;
      adult_count?: number;
      child_count?: number;
      toddler_count?: number;
    }) => {
      const { data, error } = await supabase.functions.invoke("manage-cart", {
        body: { action: "add", session_token: sessionToken, ...params },
      });
      if (error || data?.error) {
        throw new Error(data?.error || "Failed to add to cart");
      }
      setRefreshKey((k) => k + 1);
      return data;
    },
    [sessionToken]
  );

  const updateItem = useCallback(
    async (cartItemId: string, counts: { adult_count?: number; child_count?: number; toddler_count?: number }) => {
      const { data, error } = await supabase.functions.invoke("manage-cart", {
        body: { action: "update", session_token: sessionToken, cart_item_id: cartItemId, ...counts },
      });
      if (error || data?.error) {
        throw new Error(data?.error || "Failed to update item");
      }
      setRefreshKey((k) => k + 1);
    },
    [sessionToken]
  );

  const removeItem = useCallback(
    async (cartItemId: string) => {
      await supabase.functions.invoke("manage-cart", {
        body: { action: "remove", session_token: sessionToken, cart_item_id: cartItemId },
      });
      setRefreshKey((k) => k + 1);
    },
    [sessionToken]
  );

  const clearCart = useCallback(async () => {
    await supabase.functions.invoke("manage-cart", {
      body: { action: "clear", session_token: sessionToken },
    });
    setRefreshKey((k) => k + 1);
  }, [sessionToken]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return {
    items,
    itemCount,
    cartId,
    loading,
    sessionToken,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    refresh,
  };
}
